import {
    Breadcrumbs,
    Center,
    Collapse,
    Modal,
    SegmentedControl,
    Slider,
    Switch,
} from '@mantine/core';
import { useDisclosure } from '@mantine/hooks';
import {
    IconChevronDown,
    IconCircleFilled,
    IconDatabase,
    IconPackage,
    IconReload,
    IconTerminal,
} from '@tabler/icons-react';
import { useId } from 'react';
import { useTranslation } from 'react-i18next';
import {
    Button,
    ConfigurationBox,
    InputField,
    TagsField,
    TextArea,
} from '../../../../components/Atoms';
import Editor from '../../../../components/Atoms/Editor/Editor';
import {
    DeployProjectSection,
    DeployTargetNotice,
    OperationSystemOptionList,
} from '../../../../components/Molecules';
import {
    CloudDeploymentSummaryCard,
    InstanceFamilyOptionList,
} from '../../../../components/Organisms';
import { useActiveProject } from '../../../../hooks/useActiveProject';
import useBreadcrumbItems from '../../../../hooks/useBreadcrumbItems';
import usePageHeading from '../../../../hooks/usePageHeading';
import { usePricingQuery } from '../../../../hooks/usePricingQuery';
import SSHKeyConfigurationBox from './components/SSHKeyConfigurationBox/SSHKeyConfigurationBox';
import {
    InstanceFamily,
    useCloudComputeDeploymentPage,
} from './hooks/useCloudComputeDeploymentPage';

export default function CloudComputeDeploymentPage() {
    const { t } = useTranslation('cloudComputeDeployment');
    const headingRef = usePageHeading(t('page.title'));
    const { t: tValidation } = useTranslation('validation');
    const deployReasonId = useId();
    const toggleId = useId();
    const canDeploy = useActiveProject().can('vm', 'manage');
    const { data: pricingResp } = usePricingQuery();
    const backupRateNoteValue = pricingResp?.products.backup.gb_hourly_rate ?? 0.0025;
    const diskAdditionRateNoteValue =
        pricingResp?.products.instance.disk_addition_gb_monthly_rate ?? 5;
    const breadcrumbItems = useBreadcrumbItems([
        { title: t('breadcrumbs.product'), href: '#' },
        { title: t('breadcrumbs.cloudCompute'), href: '/cloud-compute' },
        { title: t('breadcrumbs.deployment'), href: '/cloud-compute/deployment' },
    ]);
    const {
        addSSHKeyModalOpened,
        backupEstimatedCost,
        computeHourlyTotalPrice,
        diskAdditionEstimatedCost,
        diskAdditionGb,
        filteredOsOptions,
        handleAddSSHKey,
        handleCloseAddSSHKeyModal,
        handleDeployInstance,
        handleGenerateHostname,
        handleHostnameChange,
        handleHostnamePaste,
        handleQuantityChange,
        maxQuantity,
        hostname,
        hostnameError,
        instanceOptions,
        isAddSSHKeySubmitting,
        isBackupEnabled,
        isDeployLoading,
        isDiskAdditionEnabled,
        isLaunchOptionsPending,
        isSSHKeyQueryLoading: isSSHKeysLoading,
        monthlyTotalPrice,
        openAddSSHKeyModal,
        osType,
        quantity,
        registerSSHKey,
        selectedInstanceFamily,
        selectedInstanceOption,
        selectedInstanceSku,
        selectedOs,
        selectedSshKeys,
        selectedVersion,
        setDiskAdditionGb,
        setIsBackupEnabled,
        setIsDiskAdditionEnabled,
        setOsType,
        setSelectedInstanceFamily,
        setSelectedInstanceSku,
        setSelectedOs,
        setSelectedSshKeys,
        setSelectedVersion,
        setStartupScript,
        sshKeyErrors,
        sshKeys,
        startupScript,
        tags,
        setTags,
        totalHourlyPrice,
    } = useCloudComputeDeploymentPage();
    const [advancedOpened, { toggle: toggleAdvanced }] = useDisclosure(false);

    return (
        <>
            <div className="w-full min-h-full flex flex-col justify-start items-center gap-8">
                <div className="w-full flex flex-col justify-start items-start">
                    <Breadcrumbs separatorMargin={'xs'} className="mb-6">
                        {breadcrumbItems}
                    </Breadcrumbs>
                    <div className="w-full flex flex-row justify-between">
                        <div className="flex flex-col justify-start items-start gap-1">
                            <h1 ref={headingRef} tabIndex={-1} className="text-3xl font-bold">
                                {t('page.title')}
                            </h1>
                            <p className="w-auto max-w-135 text-base text-fg/68">
                                {t('page.subtitle')}
                            </p>
                        </div>
                    </div>
                </div>
                <div className="w-full h-auto grid grid-cols-1 2xl:grid-cols-[2.5fr_1fr] items-start gap-4">
                    <div className="w-full h-auto grid grid-cols-1 justify-self-center items-center gap-4">
                        <DeployProjectSection product="vm" />
                        <ConfigurationBox
                            label={t('steps.pickOs')}
                            leftDescription={t('steps.pickOsDescription')}
                        >
                            <div className="w-full h-auto flex flex-col justify-start items-start gap-4">
                                <SegmentedControl
                                    aria-label={t('steps.osType')}
                                    size={'sm'}
                                    radius={'md'}
                                    value={osType}
                                    onChange={(value) => {
                                        setOsType(value as 'linux' | 'windows');
                                        setSelectedOs(null);
                                        setSelectedVersion(null);
                                    }}
                                    styles={{
                                        root: {
                                            backgroundColor: 'var(--surface-2)',
                                            border: '0.5px solid var(--line)',
                                        },
                                    }}
                                    withItemsBorders={false}
                                    data={[
                                        {
                                            value: 'linux',
                                            label: (
                                                <Center style={{ gap: 4 }}>
                                                    <IconTerminal size={16} />
                                                    <span style={{ fontWeight: 400 }}>
                                                        {t('osType.linux')}
                                                    </span>
                                                </Center>
                                            ),
                                        },
                                        {
                                            value: 'windows',
                                            label: (
                                                <Center style={{ gap: 4 }}>
                                                    <IconPackage size={16} />
                                                    <span style={{ fontWeight: 400 }}>
                                                        {t('osType.windows')}
                                                    </span>
                                                </Center>
                                            ),
                                        },
                                    ]}
                                />
                                <OperationSystemOptionList
                                    label={t('steps.pickOs')}
                                    options={filteredOsOptions}
                                    selectedOsSlug={selectedOs?.slug}
                                    onSelectOs={(os) => {
                                        setSelectedOs(os);
                                        setSelectedVersion(os.versions?.[0]?.slug || null);
                                    }}
                                    selectedVersionSlug={selectedVersion}
                                    onSelectVersion={(os, version) => {
                                        setSelectedOs(os);
                                        setSelectedVersion(version);
                                    }}
                                    versionPlaceholder={t('steps.selectVersion')}
                                    isLoading={isLaunchOptionsPending}
                                />
                            </div>
                        </ConfigurationBox>
                        <ConfigurationBox
                            label={t('steps.instanceFamily')}
                            leftDescription={t('steps.instanceFamilyDescription')}
                            rightDescription={
                                <div className="inline-flex items-center text-sm gap-1.5">
                                    <span className="text-fg/68">
                                        {t('instanceFamilyOption.showing')}
                                    </span>
                                    <span className="font-bold text-fg">
                                        {selectedInstanceFamily === 'shared'
                                            ? t('instanceFamilyOption.shared')
                                            : selectedInstanceFamily === 'general-purpose'
                                              ? t('instanceFamilyOption.generalPurpose')
                                              : t('instanceFamilyOption.dedicated')}
                                    </span>
                                </div>
                            }
                            footer={
                                <span className="text-yellow-400/80 light:text-yellow-800">
                                    {t('instanceFamilyOption.footerNote')}
                                </span>
                            }
                        >
                            <div className="w-full">
                                <SegmentedControl
                                    aria-label={t('steps.instanceFamily')}
                                    fullWidth
                                    size="sm"
                                    radius={'md'}
                                    value={selectedInstanceFamily}
                                    onChange={(value) => {
                                        setSelectedInstanceFamily(value as InstanceFamily);
                                        setSelectedInstanceSku(null);
                                    }}
                                    styles={{
                                        root: {
                                            backgroundColor: 'var(--surface-2)',
                                            border: '0.5px solid var(--line)',
                                        },
                                    }}
                                    withItemsBorders={false}
                                    data={[
                                        {
                                            value: 'shared',
                                            label: (
                                                <Center style={{ gap: 6 }}>
                                                    <IconCircleFilled
                                                        size={8}
                                                        color="oklch(78.9% 0.154 211.53)"
                                                    />
                                                    <div className="flex flex-row justify-center items-center gap-1.5">
                                                        <span>
                                                            {t('instanceFamilyOption.shared')}
                                                        </span>
                                                        <span
                                                            style={{ fontWeight: 400 }}
                                                            className="hidden md:inline text-xs text-fg/68"
                                                        >
                                                            {t('instanceFamilyOption.sharedHint')}
                                                        </span>
                                                    </div>
                                                </Center>
                                            ),
                                        },
                                        {
                                            value: 'general-purpose',
                                            label: (
                                                <Center style={{ gap: 6 }}>
                                                    <IconCircleFilled
                                                        size={8}
                                                        color="oklch(72.3% 0.219 149.579)"
                                                    />
                                                    <div className="flex flex-row justify-center items-center gap-1.5">
                                                        <span>
                                                            {t(
                                                                'instanceFamilyOption.generalPurpose'
                                                            )}
                                                        </span>
                                                        <span
                                                            style={{ fontWeight: 400 }}
                                                            className="hidden md:inline text-xs text-fg/68"
                                                        >
                                                            {t(
                                                                'instanceFamilyOption.generalPurposeHint'
                                                            )}
                                                        </span>
                                                    </div>
                                                </Center>
                                            ),
                                        },
                                        {
                                            value: 'dedicated',
                                            label: (
                                                <Center style={{ gap: 6 }}>
                                                    <IconCircleFilled
                                                        size={8}
                                                        color="oklch(58.5% 0.233 277.117)"
                                                    />
                                                    <div className="flex flex-row justify-center items-center gap-1.5">
                                                        <span>
                                                            {t('instanceFamilyOption.dedicated')}
                                                        </span>
                                                        <span
                                                            style={{ fontWeight: 400 }}
                                                            className="hidden md:inline text-xs text-fg/68"
                                                        >
                                                            {t(
                                                                'instanceFamilyOption.dedicatedHint'
                                                            )}
                                                        </span>
                                                    </div>
                                                </Center>
                                            ),
                                        },
                                    ]}
                                />
                            </div>
                            <div className="w-full mt-4">
                                <InstanceFamilyOptionList
                                    options={instanceOptions}
                                    selectedSku={selectedInstanceSku || undefined}
                                    onSelectInstance={setSelectedInstanceSku}
                                    isLoading={isLaunchOptionsPending}
                                />
                            </div>
                        </ConfigurationBox>
                        <ConfigurationBox
                            label={t('steps.hostnameAndTags')}
                            leftDescription={t('steps.hostnameAndTagsDescription')}
                        >
                            <div className="w-full flex flex-col justify-center items-center gap-4">
                                <div className="w-full flex flex-row justify-between items-start gap-4">
                                    <InputField
                                        size="md"
                                        label={<h3 className="text-base">{t('hostname.label')}</h3>}
                                        placeholder={t('hostname.placeholder')}
                                        value={hostname}
                                        pattern="[A-Za-z0-9-]+"
                                        minLength={4}
                                        maxLength={20}
                                        onChange={(e) => handleHostnameChange(e.target.value || '')}
                                        onPaste={handleHostnamePaste}
                                        error={hostnameError}
                                        rightIcon={
                                            <button
                                                type="button"
                                                onClick={handleGenerateHostname}
                                                aria-label={t('hostname.generateAriaLabel')}
                                                style={{
                                                    display: 'inline-flex',
                                                    alignItems: 'center',
                                                    justifyContent: 'center',
                                                    padding: 0,
                                                    border: 'none',
                                                    background: 'transparent',
                                                    color: 'var(--color-text-muted, currentColor)',
                                                    cursor: 'pointer',
                                                    opacity: 0.7,
                                                    transition: 'opacity 0.15s ease',
                                                }}
                                                onMouseEnter={(e) =>
                                                    (e.currentTarget.style.opacity = '1')
                                                }
                                                onMouseLeave={(e) =>
                                                    (e.currentTarget.style.opacity = '0.7')
                                                }
                                            >
                                                <IconReload size={20} />
                                            </button>
                                        }
                                        descriptionBottom={t('hostname.description')}
                                    />
                                    <InputField
                                        size="md"
                                        type="number"
                                        max={maxQuantity}
                                        min={1}
                                        value={quantity}
                                        onChange={(e) =>
                                            handleQuantityChange(e.currentTarget.value)
                                        }
                                        label={<h3 className="text-base">{t('quantity.label')}</h3>}
                                        descriptionBottom={t('quantity.description')}
                                    />
                                </div>
                                <TagsField
                                    label={<h3 className="text-base">{t('tags.label')}</h3>}
                                    value={tags}
                                    onAdd={(t) => setTags([...tags, t])}
                                    onRemove={(t) => setTags(tags.filter((x) => x !== t))}
                                    placeholder={t('tags.placeholder')}
                                    description={t('tags.description')}
                                    maxTags={5}
                                />
                            </div>
                        </ConfigurationBox>
                        <button
                            type="button"
                            onClick={toggleAdvanced}
                            aria-expanded={advancedOpened}
                            className="w-full p-4 flex flex-row justify-between items-center bg-surface-1 border-[0.5px] border-line rounded-md cursor-pointer"
                        >
                            <div className="flex flex-col justify-start items-start gap-1 text-left">
                                <h2 className="text-lg font-bold">
                                    {t('steps.advancedConfiguration')}
                                </h2>
                                <p className="text-sm text-fg/68">
                                    {t('steps.advancedConfigurationDescription')}
                                </p>
                            </div>
                            <IconChevronDown
                                size={20}
                                className="transition-transform duration-200"
                                style={{ transform: advancedOpened ? 'rotate(180deg)' : 'none' }}
                            />
                        </button>
                        <Collapse expanded={advancedOpened}>
                            <div className="w-full flex flex-col gap-4">
                                {osType !== 'windows' && (
                                    <>
                                        <ConfigurationBox
                                            label={t('steps.sshKey')}
                                            leftDescription={t('steps.sshKeyDescription')}
                                            rightDescription={
                                                <div className="text-sm">
                                                    <span className="font-bold text-fg">
                                                        {selectedSshKeys.length}{' '}
                                                    </span>
                                                    <span className="text-fg/68">
                                                        {t('sshKey.selected')}
                                                    </span>
                                                </div>
                                            }
                                        >
                                            <SSHKeyConfigurationBox
                                                sshKeys={sshKeys}
                                                selectedKeys={selectedSshKeys}
                                                onChangeSelectedKeys={setSelectedSshKeys}
                                                isLoading={isSSHKeysLoading}
                                                onAddNewKey={openAddSSHKeyModal}
                                            />
                                        </ConfigurationBox>
                                        <ConfigurationBox
                                            label={t('steps.startupScript')}
                                            leftDescription={t('steps.startupScriptDescription')}
                                        >
                                            <Editor
                                                initialValue={startupScript}
                                                filename={t('startupScript.filename')}
                                                placeholder={t('startupScript.placeholder')}
                                                onChange={setStartupScript}
                                            />
                                        </ConfigurationBox>
                                    </>
                                )}
                                <ConfigurationBox
                                    label={t('steps.diskAddition')}
                                    leftDescription={t('steps.diskAdditionDescription')}
                                >
                                    <div className="w-full h-auto p-4 flex flex-col justify-start items-start gap-3 border-[0.5px] rounded-md border-line">
                                        <div className="w-full flex flex-row justify-between items-center">
                                            <div className="flex flex-row justify-start items-center gap-4">
                                                <IconDatabase size={36} />
                                                <div className="flex flex-col justify-start items-start">
                                                    <h3
                                                        id={`${toggleId}-diskAddition-title`}
                                                        className="text-base"
                                                    >
                                                        {t('diskAddition.enableTitle')}
                                                    </h3>
                                                    <p
                                                        id={`${toggleId}-diskAddition-description`}
                                                        className="text-sm text-fg/68"
                                                    >
                                                        {t('diskAddition.enableDescription')}
                                                    </p>
                                                    <span
                                                        id={`${toggleId}-diskAddition-rate`}
                                                        className="mt-1.5 text-sm text-yellow-500/80 light:text-yellow-800"
                                                    >
                                                        {t('diskAddition.rateNote', {
                                                            rate: diskAdditionRateNoteValue,
                                                        })}
                                                    </span>
                                                </div>
                                            </div>
                                            <Switch
                                                aria-labelledby={`${toggleId}-diskAddition-title`}
                                                aria-describedby={`${toggleId}-diskAddition-description ${toggleId}-diskAddition-rate`}
                                                size="lg"
                                                checked={isDiskAdditionEnabled}
                                                onChange={(event) =>
                                                    setIsDiskAdditionEnabled(
                                                        event.currentTarget.checked
                                                    )
                                                }
                                            />
                                        </div>
                                        {isDiskAdditionEnabled && (
                                            <div className="w-full flex flex-col justify-start items-start gap-2 pt-1">
                                                <div className="w-full flex flex-row justify-between items-center">
                                                    <span className="text-sm text-fg/68">
                                                        {t('diskAddition.sliderLabel')}
                                                    </span>
                                                    <span className="text-sm font-bold">
                                                        {diskAdditionGb} GB
                                                    </span>
                                                </div>
                                                <Slider
                                                    className="w-full"
                                                    color="blue"
                                                    min={1}
                                                    max={512}
                                                    value={diskAdditionGb}
                                                    onChange={setDiskAdditionGb}
                                                    label={(value) => `${value} GB`}
                                                    marks={[
                                                        { value: 1, label: '1' },
                                                        { value: 128, label: '128' },
                                                        { value: 256, label: '256' },
                                                        { value: 384, label: '384' },
                                                        { value: 512, label: '512' },
                                                    ]}
                                                    mb="md"
                                                />
                                            </div>
                                        )}
                                    </div>
                                </ConfigurationBox>
                                <ConfigurationBox
                                    label={t('steps.backup')}
                                    leftDescription={t('steps.backupDescription')}
                                >
                                    <div className="w-full h-auto p-4 flex flex-col justify-start items-start gap-3 border-[0.5px] rounded-md border-line">
                                        <div className="w-full flex flex-row justify-between items-center">
                                            <div className="flex flex-row justify-start items-center gap-4">
                                                <IconReload size={36} />
                                                <div className="flex flex-col justify-start items-start">
                                                    <h3
                                                        id={`${toggleId}-backup-title`}
                                                        className="text-base"
                                                    >
                                                        {t('backup.enableTitle')}
                                                    </h3>
                                                    <p
                                                        id={`${toggleId}-backup-description`}
                                                        className="text-sm text-fg/68"
                                                    >
                                                        {t('backup.enableDescription')}
                                                    </p>
                                                    <span
                                                        id={`${toggleId}-backup-rate`}
                                                        className="mt-1.5 text-sm text-yellow-500/80 light:text-yellow-800"
                                                    >
                                                        {t('backup.rateNote', {
                                                            rate: backupRateNoteValue,
                                                        })}
                                                    </span>
                                                </div>
                                            </div>
                                            <Switch
                                                aria-labelledby={`${toggleId}-backup-title`}
                                                aria-describedby={`${toggleId}-backup-description ${toggleId}-backup-rate`}
                                                size="lg"
                                                checked={isBackupEnabled}
                                                onChange={(event) =>
                                                    setIsBackupEnabled(event.currentTarget.checked)
                                                }
                                            />
                                        </div>
                                    </div>
                                </ConfigurationBox>
                            </div>
                        </Collapse>
                    </div>
                    <CloudDeploymentSummaryCard
                        backupEstimatedCost={backupEstimatedCost}
                        computeHourlyTotalPrice={computeHourlyTotalPrice}
                        diskAdditionEstimatedCost={diskAdditionEstimatedCost}
                        diskAdditionGb={diskAdditionGb}
                        hostname={hostname}
                        hourlyTotalPrice={totalHourlyPrice}
                        isBackupEnabled={isBackupEnabled}
                        isDeployLoading={isDeployLoading}
                        isDiskAdditionEnabled={isDiskAdditionEnabled}
                        monthlyTotalPrice={monthlyTotalPrice}
                        onDeploy={handleDeployInstance}
                        quantity={quantity}
                        selectedInstanceFamily={selectedInstanceFamily}
                        selectedInstanceOption={selectedInstanceOption}
                        selectedVersion={selectedVersion}
                        osType={osType}
                        notice={<DeployTargetNotice product="vm" reasonId={deployReasonId} />}
                        deployBlockedReasonId={canDeploy ? undefined : deployReasonId}
                    />
                </div>
            </div>

            <Modal
                opened={addSSHKeyModalOpened}
                onClose={handleCloseAddSSHKeyModal}
                withCloseButton={false}
                overlayProps={{
                    backgroundOpacity: 0.55,
                    blur: 3,
                }}
                centered
                transitionProps={{ transition: 'fade', duration: 128, timingFunction: 'linear' }}
            >
                <div className="flex flex-col justify-center items-center gap-6">
                    <div className="flex flex-col justify-start items-start">
                        <Modal.Title>
                            <span className="text-xl font-bold text-fg">
                                {t('sshKey.modal.title')}
                            </span>
                        </Modal.Title>
                        <p className="text-fg/68">{t('sshKey.modal.subtitle')}</p>
                    </div>
                    <div className="w-full flex flex-col justify-start items-start gap-2">
                        <p>{t('sshKey.modal.generateCommand')}</p>
                        <Editor
                            size="sm"
                            readOnly
                            height={'60px'}
                            filename={t('sshKey.modal.generateFilename')}
                            defaultValue='ssh-keygen -t rsa -b 4096 -C "your_email@example.com"'
                            hideLineCharsLabel
                            hideScrollbarY
                            disableWrap
                            enableCopy
                        />
                    </div>

                    <form
                        autoComplete="off"
                        onSubmit={handleAddSSHKey}
                        className="w-full flex flex-col justify-start items-start"
                    >
                        <div className="w-full flex flex-col justify-start items-start gap-2">
                            <div className="flex flex-col justify-start items-start">
                                <h3 className="font-bold">{t('sshKey.modal.sectionTitle')}</h3>
                                <p className="text-sm text-fg/68">
                                    {t('sshKey.modal.sectionDescriptionPrefix')}{' '}
                                    <code>ssh-rsa</code> or <code>ssh-ed25519</code>
                                </p>
                            </div>
                            <InputField
                                size="md"
                                type="text"
                                label={t('sshKey.modal.keyNameLabel')}
                                placeholder={t('sshKey.modal.keyNamePlaceholder')}
                                {...registerSSHKey('name', {
                                    required: tValidation('sshKey.nameRequired'),
                                })}
                                error={sshKeyErrors.name?.message}
                                required
                            />
                            <TextArea
                                minRows={4}
                                maxRows={4}
                                draggable={false}
                                label={t('sshKey.modal.keyContentLabel')}
                                placeholder={t('sshKey.modal.keyContentPlaceholder')}
                                {...registerSSHKey('content', {
                                    required: tValidation('sshKey.contentRequired'),
                                    pattern: {
                                        value: /^(ssh-rsa|ssh-ed25519|ssh-dss|ecdsa-sha2-)/,
                                        message: tValidation('sshKey.contentInvalid'),
                                    },
                                })}
                                error={sshKeyErrors.content?.message}
                                required
                            />
                        </div>
                        <div className="w-full flex flex-col justify-center items-center gap-2">
                            <Button
                                width={'100%'}
                                type="submit"
                                isLoading={isAddSSHKeySubmitting}
                                className="mt-6"
                            >
                                {t('sshKey.modal.create')}
                            </Button>
                            <Button
                                width={'100%'}
                                variant="secondary"
                                onClick={handleCloseAddSSHKeyModal}
                            >
                                {t('sshKey.modal.cancel')}
                            </Button>
                        </div>
                    </form>
                </div>
            </Modal>
        </>
    );
}
